import { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { type AutocompleteResponse } from '../types/Autocomplete';
import { type BikeFactor, type RouteHistory, type MainState } from '../types/GlobalContext';

// Version actuelle du state
export const MAIN_STATE_VERSION = 7;
const DB_NAME = 'main_state_db';
const STORE_NAME = 'main_state';

type StateVersions = Record<
number,
{
  initialState: Partial<MainState>;
  compatibleProps: Array<keyof MainState>;
}
>;

const initialBikeFactor: BikeFactor = {
  timeFactor: 0.33,
  slopeFactor: 0.33,
  safetyFactor: 0.34,
  isPersonalBike: false
};

const stateVersions: StateVersions = {
  1: {
    initialState: {
      searchHistory: [],
      routeHistory: [],
      currency: 'CHF',
      version: 1
    },
    compatibleProps: []
  },
  2: {
    initialState: {
      token: null,
      userRole: null,
      version: 2
    },
    compatibleProps: ['searchHistory', 'routeHistory', 'currency']
  },
  3: {
    initialState: {
      departure: null,
      bikeFactor: initialBikeFactor,
      version: 3
    },
    compatibleProps: ['searchHistory', 'routeHistory', 'currency', 'token', 'userRole']
  },
  4: {
    initialState: {
      map: undefined,
      version: 4
    },
    compatibleProps: [
      'searchHistory',
      'routeHistory',
      'currency',
      'token',
      'userRole',
      'bikeFactor'
    ]
  },
  5: {
    initialState: {
      trackingMode: false
    },
    compatibleProps: [
      'searchHistory',
      'routeHistory',
      'currency',
      'token',
      'userRole',
      'bikeFactor'
    ]
  },
  6: {
    initialState: {

    },
    compatibleProps: [
      'searchHistory',
      'currency',
      'token',
      'userRole',
      'bikeFactor'
    ]
  },
  7: {
    initialState: {
      departure: null
    },
    compatibleProps: [
      'searchHistory',
      'currency',
      'token',
      'userRole',
      'bikeFactor'
    ]
  }
};

const buildInitialState = (): MainState => {
  let state: Partial<MainState> = {};

  for (let version = 1; version <= MAIN_STATE_VERSION; version++) {
    state = {
      ...state,
      ...stateVersions[version].initialState
    };
  }

  // Assurez-vous que les états non persistés ont des valeurs initiales
  state.routeHistory = [];

  return state as MainState;
};

const initialState = buildInitialState();

const migrateState = (
  oldState: MainState & { version?: number }
): MainState => {
  const oldVersion = oldState.version ?? 1;

  if (oldVersion === MAIN_STATE_VERSION) {
    return oldState;
  }

  const newState = { ...buildInitialState() };

  for (let version = oldVersion + 1; version <= MAIN_STATE_VERSION; version++) {
    const compatibleProps = stateVersions[version].compatibleProps;
    compatibleProps.forEach((prop) => {
      if (oldState[prop] !== undefined) {
        // @ts-expect-error mécanisme générique
        newState[prop] = oldState[prop];
      }
    });
  }

  newState.version = MAIN_STATE_VERSION;

  return newState;
};

const loadState = async (): Promise<MainState> => {
  return await new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, MAIN_STATE_VERSION);

    request.onerror = () => {
      console.error('Error loading main state:', request.error);
      resolve({ ...initialState, version: MAIN_STATE_VERSION });
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const getRequest = objectStore.get(STORE_NAME);

      getRequest.onsuccess = () => {
        if (getRequest.result !== undefined) {
          const parsedState = getRequest.result;
          const migratedState = migrateState(parsedState);
          resolve({ ...migratedState, map: undefined });
        } else {
          resolve({ ...initialState, version: MAIN_STATE_VERSION });
        }
      };

      getRequest.onerror = () => {
        console.error('Error loading main state:', getRequest.error);
        resolve({ ...initialState, version: MAIN_STATE_VERSION });
      };
    };

    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

const saveState = async (state: MainState) => {
  return await new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, MAIN_STATE_VERSION);

    request.onerror = () => {
      console.error('Error saving main state:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);

      const stateToSave = { ...state, id: STORE_NAME };

      const putRequest = objectStore.put(stateToSave);

      putRequest.onsuccess = () => {
        resolve(true);
      };

      putRequest.onerror = () => {
        console.error('Error saving main state:', putRequest.error);
        reject(putRequest.error);
      };
    };
  });
};

interface StoredContextType {
  state: MainState;
  setToken: (token: string) => void;
  clearToken: () => void;
  setSearchHistory: (item: AutocompleteResponse, isSource: boolean) => void;
  resetCurrency: () => void;
  clearDeparture: () => void;
  setDeparture: (departure: AutocompleteResponse) => void;
  removeExpiredRoutes: (timestamp: number) => void;
  setBikeFactor: (factor: BikeFactor) => void;
  clearBikeFactor: () => void;
}

const StoredContext = createContext<StoredContextType | null>(null);

export const MainProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MainState>(initialState);

  useEffect(() => {
    const fetchState = async () => {
      const savedState = await loadState();
      setState(savedState);
    };

    void fetchState();
  }, []);

  useEffect(() => {
    const save = async () => {
      try {
        await saveState(state);
      } catch (error) {
        console.error('Failed to save state:', error);
      }
    };

    void save();
  }, [state]);

  const setToken = useCallback((token: string) => {
    setState((prev) => ({ ...prev, token }));
  }, []);

  const clearToken = useCallback(() => {
    setState((prev) => ({ ...prev, token: null, userRole: null }));
  }, []);

  const setSearchHistory = useCallback(
    (item: AutocompleteResponse, isDeparture: boolean) => {
      setState((prev) => {
        const existingItemIndex = prev.searchHistory.findIndex(
          (historyItem) => historyItem.address === item.address
        );

        let newSearchHistory = [...prev.searchHistory];
        if (existingItemIndex === -1) {
          newSearchHistory.unshift({
            ...item,
            departureCount: isDeparture ? 1 : 0,
            arrivalCount: isDeparture ? 0 : 1,
            date: Date.now()
          });
        } else {
          const updatedItem = { ...newSearchHistory[existingItemIndex] };
          if (isDeparture) {
            updatedItem.departureCount += 1;
          } else {
            updatedItem.arrivalCount += 1;
          }
          updatedItem.date = Date.now();

          newSearchHistory.splice(existingItemIndex, 1);
          newSearchHistory = [updatedItem, ...newSearchHistory];
        }

        newSearchHistory.sort((a, b) => {
          if (b.departureCount !== a.departureCount) {
            return b.departureCount - a.departureCount;
          }
          return b.arrivalCount - a.arrivalCount;
        });

        return { ...prev, searchHistory: newSearchHistory };
      });
    },
    []
  );

  const resetCurrency = useCallback(() => {
    setState((prev) => ({ ...prev, currency: initialState.currency }));
  }, []);

  const setRouteHistory = useCallback((route: RouteHistory) => {
    setState((prev) => ({
      ...prev,
      routeHistory: [...prev.routeHistory, route]
    }));
  }, []);

  const clearDeparture = useCallback(() => {
    setState((prev) => ({ ...prev, departure: null }));
  }, []);

  const setDeparture = useCallback((source: AutocompleteResponse) => {
    setState((prev) => ({ ...prev, departure: source }));
  }, []);

  const removeExpiredRoutes = useCallback((timestamp: number) => {
    setState((prev) => ({
      ...prev,
      routeHistory: prev.routeHistory.filter(
        (route) => route.timestamp > timestamp
      )
    }));
  }, []);

  const setBikeFactor = useCallback((factor: BikeFactor) => {
    setState((prev) => ({ ...prev, bikeFactor: factor }));
  }, []);

  const clearBikeFactor = useCallback(() => {
    setState((prev) => ({ ...prev, bikeFactor: initialBikeFactor }));
  }, []);

  const value = {
    state,
    setToken,
    clearToken,
    setSearchHistory,
    resetCurrency,
    setRouteHistory,
    clearDeparture,
    setDeparture,
    removeExpiredRoutes,
    setBikeFactor,
    clearBikeFactor
  };

  return <StoredContext.Provider value={value}>{children}</StoredContext.Provider>;
};

export const useMain = () => {
  const context = useContext(StoredContext);
  if (context == null) {
    throw new Error('useMain must be used within a MainProvider');
  }
  return context;
};
